// Lock section of the unit-details form. Single source of truth shared by the
// Locks screen (LocksScreen) and the onboarding inspection's "Replace Unit locks"
// step (InspectScreen). Renders the fields only — the consumer wraps it in a Card.
//
// Props:
//   values   — { unitLockCode, unitLockType, unitLockCount, unitLockNotes,
//                tenantLockbox, commonLockCode, commonLockType, commonLockCount,
//                commonLockNotes } (any subset; missing fields render empty)
//   onChange — (field, value) => void
import { Lock } from 'lucide-react';
import { LOCK_CODES, LOCK_TYPES } from '../../utils/unitDetailsHelpers';
import { colors, SelectField } from './index';

const lockCodeOptions = LOCK_CODES.map(l => ({ value: l, label: l }));
const lockTypeOptions = LOCK_TYPES.map(t => ({ value: t, label: t }));

const LockFields = ({ values = {}, onChange }) => {
  const v = (field) => values[field] || '';
  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <Lock size={18} style={{ color: colors.yellow }} />
        <span className="font-semibold" style={{ color: colors.yellow }}>Lock Codes</span>
      </div>

      {/* Unit Lock */}
      <p className="text-xs font-semibold mb-2" style={{ color: colors.textMuted }}>UNIT LOCK</p>
      <SelectField
        label="Unit Lock Code"
        value={v('unitLockCode')}
        onChange={(val) => onChange('unitLockCode', val)}
        options={lockCodeOptions}
        placeholder="-- Select code --"
      />
      <SelectField
        label="Unit Lock Type"
        value={v('unitLockType')}
        onChange={(val) => onChange('unitLockType', val)}
        options={lockTypeOptions}
        placeholder="-- Select type --"
      />
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Unit Lock Count</label>
        <input
          type="number"
          value={v('unitLockCount')}
          onChange={e => onChange('unitLockCount', e.target.value)}
          placeholder="Number of unit locks"
          className="w-full px-4 py-3 rounded-xl outline-none"
          style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
        />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Unit Lock Notes</label>
        <textarea
          value={v('unitLockNotes')}
          onChange={e => onChange('unitLockNotes', e.target.value)}
          placeholder="Notes about unit locks..."
          rows={2}
          className="w-full px-4 py-3 rounded-xl outline-none resize-none"
          style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
        />
      </div>

      {/* Tenant Lockbox */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Tenant Lockbox Code</label>
        <input
          type="text"
          value={v('tenantLockbox')}
          onChange={e => onChange('tenantLockbox', e.target.value)}
          placeholder="None or code"
          className="w-full px-4 py-3 rounded-xl outline-none"
          style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
        />
      </div>

      {/* Common Lock */}
      <p className="text-xs font-semibold mb-2 mt-6" style={{ color: colors.textMuted }}>COMMON LOCK</p>
      <SelectField
        label="Common Lock Code"
        value={v('commonLockCode')}
        onChange={(val) => onChange('commonLockCode', val)}
        options={lockCodeOptions}
        placeholder="-- Select code --"
      />
      <SelectField
        label="Common Lock Type"
        value={v('commonLockType')}
        onChange={(val) => onChange('commonLockType', val)}
        options={lockTypeOptions}
        placeholder="-- Select type --"
      />
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Common Lock Count</label>
        <input
          type="number"
          value={v('commonLockCount')}
          onChange={e => onChange('commonLockCount', e.target.value)}
          placeholder="Number of common locks"
          className="w-full px-4 py-3 rounded-xl outline-none"
          style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
        />
      </div>
      <div className="mb-0">
        <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Common Lock Notes</label>
        <textarea
          value={v('commonLockNotes')}
          onChange={e => onChange('commonLockNotes', e.target.value)}
          placeholder="Notes about common locks..."
          rows={2}
          className="w-full px-4 py-3 rounded-xl outline-none resize-none"
          style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
        />
      </div>
    </>
  );
};

export default LockFields;
